import { ApplicationConfig, LOCALE_ID, isDevMode, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import es from '@angular/common/locales/es';
import en from '@angular/common/locales/en';
import { provideNzI18n, es_ES, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { es as dateFnsEs } from 'date-fns/locale';

import { authInterceptor } from './core/auth/auth.interceptor';
import { connectivityHttpInterceptor } from './core/sync/connectivity-http.interceptor';
import { localeHttpInterceptor } from './core/i18n/locale-http.interceptor';
import {
  AlertOutline,
  AppstoreOutline,
  AppleFill,
  ArrowLeftOutline,
  BellOutline,
  CalendarOutline,
  CameraOutline,
  CheckOutline,
  CloseCircleOutline,
  CloudOutline,
  CoffeeOutline,
  ColumnWidthOutline,
  DatabaseOutline,
  DeleteOutline,
  DownloadOutline,
  DownOutline,
  EditOutline,
  ExclamationCircleOutline,
  ExperimentOutline,
  EyeInvisibleOutline,
  EyeOutline,
  FileExcelOutline,
  FilePdfOutline,
  FileTextOutline,
  FileWordOutline,
  HeartOutline,
  HomeOutline,
  IdcardOutline,
  LeftOutline,
  LoadingOutline,
  LogoutOutline,
  MailOutline,
  ManOutline,
  MedicineBoxOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  PhoneOutline,
  PictureOutline,
  PieChartOutline,
  PlusOutline,
  PrinterOutline,
  ReadOutline,
  RightOutline,
  SafetyCertificateOutline,
  SaveOutline,
  SearchOutline,
  SettingOutline,
  SolutionOutline,
  SyncOutline,
  TableOutline,
  TeamOutline,
  ThunderboltOutline,
  UnorderedListOutline,
  UpOutline,
  UserAddOutline,
  UserOutline,
  WifiOutline,
} from '@ant-design/icons-angular/icons';

import { routes } from './app.routes';

registerLocaleData(es);
registerLocaleData(en);

// NG-ZORRO tree-shake: cada icono se registra UNA vez por tema (outline/fill/twotone).
// Si en el template usas <span nz-icon nzType="X" nzTheme="fill"> tienes que
// importar XFill explicitamente; XOutline no cubre fill.
const ICONS = [
  AlertOutline,
  AppstoreOutline,
  AppleFill,
  ArrowLeftOutline,
  BellOutline,
  CalendarOutline,
  CameraOutline,
  CheckOutline,
  CloseCircleOutline,
  CloudOutline,
  CoffeeOutline,
  ColumnWidthOutline,
  DatabaseOutline,
  DeleteOutline,
  DownloadOutline,
  DownOutline,
  EditOutline,
  ExclamationCircleOutline,
  ExperimentOutline,
  EyeInvisibleOutline,
  EyeOutline,
  FileExcelOutline,
  FilePdfOutline,
  FileTextOutline,
  FileWordOutline,
  HeartOutline,
  HomeOutline,
  IdcardOutline,
  LeftOutline,
  LoadingOutline,
  LogoutOutline,
  MailOutline,
  ManOutline,
  MedicineBoxOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  PhoneOutline,
  PictureOutline,
  PieChartOutline,
  PlusOutline,
  PrinterOutline,
  ReadOutline,
  RightOutline,
  SafetyCertificateOutline,
  SaveOutline,
  SearchOutline,
  SettingOutline,
  SolutionOutline,
  SyncOutline,
  TableOutline,
  TeamOutline,
  ThunderboltOutline,
  UnorderedListOutline,
  UpOutline,
  UserAddOutline,
  UserOutline,
  WifiOutline,
];

// Service worker en builds de produccion (PWA).
export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es-MX' },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([localeHttpInterceptor, authInterceptor, connectivityHttpInterceptor]),
    ),
    provideNzI18n(es_ES),
    { provide: NZ_DATE_LOCALE, useValue: dateFnsEs },
    provideNzIcons(ICONS),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
